import { FC, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { observer } from "mobx-react-lite";
import { useQuery } from "convex/react";
import { api } from "@/smart-views/client/api";
import { isConvexConfigured } from "@/smart-views/client/convex-client";
import { setSmartViewTitles } from "@/smart-views/client/title-cache";
import { SMART_VIEW_ICON_KIND } from "@/smart-views/icons";
import { CreateSmartViewModal } from "@/smart-views/components/CreateSmartViewModal";
import { useAppStore } from "@/store";
import { generateRecentDateString } from "@/domains/date/date";
import { hasSmartViewUnreadUpdate } from "@/smart-views/client/update-notifications";
import {
  MDSButton,
  MDSButtonVariant,
  MDSFadeBottom,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconKind,
  MDSPanelContainer,
  MDSPanelHeader,
  MDSPanelHeaderButtonGroup,
  MDSPanelHeaderSlotName,
  MDSText,
  MDSTextColor,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTextWeight,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";

export const SmartViewsListPage: FC = observer(function SmartViewsListPage() {
  const { store } = useAppStore();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const smartViews = useQuery(
    api.smartViews.client.listSmartViews,
    isConvexConfigured() ? {} : "skip"
  );

  useEffect(() => {
    if (smartViews) setSmartViewTitles(smartViews);
  }, [smartViews]);

  const openSmartView = (smartViewId: string) => {
    store.navigation.goToSmartView({ smartViewId, openInSameTab: true });
  };

  return (
    <PageWrapper>
      <MDSPanelHeader>
        <MDSText
          slot={MDSPanelHeaderSlotName.Heading}
          variant={MDSTextVariant.HeaderXl}
          numberOfLines={1}
        >
          Smart Views
        </MDSText>
        <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
          <MDSTooltip config={{ label: "Create smart view", placement: MDSTooltipPlacement.Top }}>
            <MDSIconButton
              iconKind={MDSIconKind.Plus}
              onClick={() => setIsCreateOpen(true)}
              size={MDSIconButtonSize.Medium}
            />
          </MDSTooltip>
        </MDSPanelHeaderButtonGroup>
      </MDSPanelHeader>
      <MDSPanelContainer>
        <ListSurface>
          {!isConvexConfigured() && (
            <EmptyState>
              <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
                Smart Views are unavailable
              </MDSText>
              <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Tertiary}>
                Smart Views are temporarily unavailable.
              </MDSText>
            </EmptyState>
          )}
          {isConvexConfigured() && smartViews === undefined && (
            <EmptyState>
              <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Tertiary}>
                Loading Smart Views...
              </MDSText>
            </EmptyState>
          )}
          {isConvexConfigured() && smartViews?.length === 0 && (
            <EmptyState>
              <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
                No Smart Views yet
              </MDSText>
              <MDSButton variant={MDSButtonVariant.Contrast} onClick={() => setIsCreateOpen(true)}>
                <MDSIcon kind={MDSIconKind.Plus} />
                Create smart view
              </MDSButton>
            </EmptyState>
          )}
          {smartViews && smartViews.length > 0 && (
            <List>
              {smartViews.map(view => (
                <SmartViewRow
                  key={view._id}
                  $hasUnreadUpdate={hasSmartViewUnreadUpdate(view.smartViewId)}
                  onClick={() => openSmartView(view.smartViewId)}
                >
                  <RowIcon>
                    <MDSIcon kind={SMART_VIEW_ICON_KIND} />
                  </RowIcon>
                  <RowText>
                    <MDSText
                      variant={MDSTextVariant.TextM}
                      weight={MDSTextWeight.SemiBold}
                      overflow={MDSTextOverflow.Ellipsis}
                      numberOfLines={1}
                    >
                      {view.title}
                    </MDSText>
                    {view.description && (
                      <MDSText
                        variant={MDSTextVariant.ParagraphSm}
                        color={MDSTextColor.Tertiary}
                        overflow={MDSTextOverflow.Ellipsis}
                        numberOfLines={1}
                      >
                        {view.description}
                      </MDSText>
                    )}
                  </RowText>
                  <MDSText
                    variant={MDSTextVariant.TextXs}
                    color={MDSTextColor.Tertiary}
                    numberOfLines={1}
                  >
                    Updated {formatUpdatedAt(view.updatedAt)}
                  </MDSText>
                </SmartViewRow>
              ))}
            </List>
          )}
          <MDSFadeBottom />
        </ListSurface>
      </MDSPanelContainer>
      <CreateSmartViewModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </PageWrapper>
  );
});

const formatUpdatedAt = (updatedAt: number): string => {
  return generateRecentDateString(DateTime.fromMillis(updatedAt), {
    skipFullDayName: true,
    relativeDateShouldBeLowercase: true,
  });
};

const PageWrapper = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ListSurface = styled.div`
  position: relative;
  height: 100%;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing["4"]};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 840px;
  margin: 0 auto;
`;

const SmartViewRow = styled.button<{ $hasUnreadUpdate?: boolean }>`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) max-content;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["3"]};
  width: 100%;
  min-height: 72px;
  padding: ${({ theme }) => theme.spacing["3"]};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ $hasUnreadUpdate, theme }) =>
    $hasUnreadUpdate
      ? theme.colors.background.banner.emphasis
      : theme.colors.background.list.default};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.background.list.hover};
    box-shadow: inset 0 0 0 9999px ${({ theme }) => theme.colors.background.list.hover};
  }
`;

const RowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  min-width: 0;
`;

const EmptyState = styled.div`
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing["3"]};
  text-align: center;
`;
